/**
 * Month arithmetic and the small vocabulary the diary screens share.
 *
 * ===========================================================================
 * DAYS ARE BUCKETED IN LOCAL TIME, NOT UTC
 * ===========================================================================
 * `starts_at` arrives as UTC. `toISOString().slice(0, 10)` is the obvious way to get a
 * day key out of it and is wrong for exactly the people who care: a 09:00 hearing in
 * Auckland lands on the previous day's square, and a 20:00 meeting in California lands
 * on the next one. So the key is built from the LOCAL calendar fields, which is the day
 * the person reading the screen means when they say "Tuesday".
 *
 * `CalendarEvent.timezone` is a separate matter — it is the zone the event was entered
 * against, is frequently empty, and is displayed rather than used for placement. Making
 * the grid honour it per-event would put two entries on different squares for the same
 * instant, which is worse.
 * ===========================================================================
 */

/** Local `YYYY-MM-DD`. Never `toISOString()` — see the module header. */
export function localDayKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export interface GridDay {
  date: Date;
  key: string;
  /** False for the leading and trailing days borrowed from the neighbouring months. */
  inMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
}

/**
 * Weeks start on Monday.
 *
 * Stated rather than derived: `Intl` exposes first-day-of-week only through
 * `Locale.prototype.getWeekInfo`, which is not available everywhere this runs, and a
 * diary that silently changes shape between two colleagues' browsers is worse than one
 * that picks a convention and keeps it. Court listings run Monday to Friday.
 */
const WEEK_STARTS_ON = 1;

function startOfWeek(date: Date): Date {
  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const shift = (result.getDay() - WEEK_STARTS_ON + 7) % 7;
  result.setDate(result.getDate() - shift);
  return result;
}

/** Six weeks, always — a grid that changes height as you page through months jumps. */
export function buildMonthGrid(anchor: Date): GridDay[] {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const cursor = startOfWeek(first);
  const todayKey = localDayKey(new Date());

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + index);
    const weekday = date.getDay();
    return {
      date,
      key: localDayKey(date),
      inMonth: date.getMonth() === anchor.getMonth(),
      isToday: localDayKey(date) === todayKey,
      isWeekend: weekday === 0 || weekday === 6,
    };
  });
}

/**
 * The window to ask the API for.
 *
 * The whole VISIBLE grid, not just the month: the leading and trailing squares are on
 * screen, and a hearing that is drawn as an empty square because the query stopped at
 * the first of the month is a diary quietly lying about a court date.
 *
 * `from`/`to` go to the server as ISO with a `Z`, which `parse_moment` accepts without
 * needing the plus-sign repair it carries for offsets.
 */
export function gridRange(anchor: Date): { from: string; to: string } {
  // `buildMonthGrid` always returns 42 cells, but `noUncheckedIndexedAccess` does not
  // know that and is right not to trust a comment about it.
  const grid = buildMonthGrid(anchor);
  const first = grid.at(0)?.date ?? new Date(anchor);
  const last = grid.at(-1)?.date ?? new Date(anchor);
  const from = new Date(first.getFullYear(), first.getMonth(), first.getDate(), 0, 0, 0, 0);
  const to = new Date(last.getFullYear(), last.getMonth(), last.getDate(), 23, 59, 59, 999);
  return { from: from.toISOString(), to: to.toISOString() };
}

export function addMonths(anchor: Date, delta: number): Date {
  return new Date(anchor.getFullYear(), anchor.getMonth() + delta, 1);
}

export function monthLabel(anchor: Date): string {
  return new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(anchor);
}

/** Short weekday headings, in the reader's locale, starting Monday. */
export function weekdayLabels(): string[] {
  const formatter = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
  // 2024-01-01 was a Monday, so 1..7 January 2024 is Monday to Sunday.
  return Array.from({ length: 7 }, (_, index) => formatter.format(new Date(2024, 0, 1 + index)));
}

/** Time only, for a grid square where the date is already the square. */
export function timeLabel(iso: string, allDay: boolean): string {
  if (allDay) return 'All day';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(date);
}

/** `CalendarEvent.EventType` in `domains/calendar/models.py`. */
export const EVENT_TYPE_OPTIONS = [
  { value: 'hearing', label: 'Hearing' },
  { value: 'appointment', label: 'Appointment' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'deadline', label: 'Deadline' },
];

export const EVENT_TYPE_FILTER_OPTIONS = [
  { value: '', label: 'Any kind' },
  ...EVENT_TYPE_OPTIONS,
];

export const EVENT_STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Any status' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rescheduled', label: 'Rescheduled' },
];

export type EventTone = 'danger' | 'primary' | 'info' | 'neutral';

/** A hearing is the one that carries consequence, so it is the one allowed to stand out. */
export function eventTone(eventType: string): EventTone {
  switch (eventType) {
    case 'hearing':
      return 'danger';
    case 'deadline':
      return 'primary';
    case 'appointment':
      return 'info';
    default:
      return 'neutral';
  }
}
